import CallIcon from '@mui/icons-material/Call';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import UpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Badge,
  Button,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { appLayoutChange } from 'FRONTEND/actions/app';
import { goToPatientForm } from 'FRONTEND/reducers/calendarSlice';
import { hideRightPanel, maxReduceRightPanel, showRightPanel } from 'FRONTEND/reducers/rightPanelSlice';
import useSize from 'FRONTEND/utils/hooks/useSize';
import i18n from 'FRONTEND/utils/i18n';
import { CALENDAR_PATH, PATIENT_APPOINTMENT_CREATION_PATH } from 'FRONTEND/utils/routes';
import { isSpecialKey } from 'LEGACY/compatibility';
import { DROPDOWN_ARROW_ANIMATION_TRANSITION, PJAX_MAIN_CONTAINER_ID, SCREEN_SM_PX, SCREEN_XS_PX, SIDE_PANEL_AUTO_REDUCE_MAX_WIDTH } from 'LEGACY/constants';

const isCalendarHref = (href) => {
  return href === CALENDAR_PATH;
};

const isPatientAppointmentCreationHref = (href) => {
  return href === PATIENT_APPOINTMENT_CREATION_PATH;
};

const isCurrentTab = (tab) => {
  const currentUrl = window.location.pathname;
  const consultations = currentUrl.includes('patients') && currentUrl.includes('consultations');

  // Special case for /patients/my-files/ to avoid matching with /patients/
  if (currentUrl.includes('/my-files/')) {
    return tab.href.includes('/my-files/');
  }

  return (consultations && tab.href.includes('consultations')) || (!consultations && currentUrl.startsWith(tab.href));
};
const getCurrentTab = (tabs) => {
  const index = tabs.findIndex(isCurrentTab);
  if (index !== -1) {
    return index;
  } else {
    return false;
  }
};

const NavBar = ({
  tabs = [],
  videoconference,
  branding,
  hasSupportHref = false,
}) => {
  const dispatch = useDispatch();
  const isVideoMaxExtended = useSelector((state) => state.rightPanel.isMaxExtended);
  const isRightPanelShown = useSelector((state) => state.rightPanel.isShown);
  const hasPendingAppointments = useSelector((state) => state.appointment.proposedCount > 0);
  const locale = useSelector((state) => state.user.locale);
  const [currentTabIndex, setCurrentTabIndex] = useState(getCurrentTab(tabs));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const screenXS = useMediaQuery(`(max-width:${SCREEN_XS_PX})`, { noSsr: true });
  const screenSM = useMediaQuery(`(max-width:${SCREEN_SM_PX})`, { noSsr: true });

  // Global responsive management when sidepanel is not present. For sidepanel handling, see SidePanel.jsx
  const mainContainer = document.getElementById(PJAX_MAIN_CONTAINER_ID); // Container without sidepanel.
  const isSidePanelMounted = useSelector((state) => state.sidePanel.isMounted);
  const contentContainerWidth = useSize({ current: mainContainer });
  useEffect(() => {
    if (contentContainerWidth > 0 && !isSidePanelMounted) {
      dispatch(appLayoutChange({
        isSmall: contentContainerWidth < SIDE_PANEL_AUTO_REDUCE_MAX_WIDTH,
      }));
    }
  }, [contentContainerWidth, isSidePanelMounted]);

  useEffect(() => {
    const shouldCollapseOnStart = !isRightPanelShown;
    if (!screenXS && !shouldCollapseOnStart) {
      dispatch(showRightPanel());
    }
    $(document).on('pjax:end', () => setCurrentTabIndex(getCurrentTab(tabs)));
  }, []);

  const handleTabClick = (event, newTabIndex, href) => {
    if (isSpecialKey(event)) {
      return;
    }
    setCurrentTabIndex(newTabIndex);
    if (isPatientAppointmentCreationHref(href)) {
      dispatch(goToPatientForm());
    }
    handleMenuClose();
    if (isVideoMaxExtended) {
      dispatch(maxReduceRightPanel());
    }
  };

  const handleMenuButtonClick = () => {
    setIsDrawerOpen(true);
  };

  const handleMenuClose = () => {
    setIsDrawerOpen(false);
  };

  const handleVideoconferenceClick = () => {
    if (isRightPanelShown) {
      dispatch(hideRightPanel());
    } else {
      dispatch(showRightPanel());
    }
  };

  const videoConsultationButtonIconSx = {
    transform: `rotate(${isRightPanelShown ? '0' : '180'}deg)`,
    transition: DROPDOWN_ARROW_ANIMATION_TRANSITION,
  };

  return (
    <div className='navBar-content'>
      <div className='menu-branding'>
        {
          branding.href ? (
            <a data-pjax className='navbar-brand' href={branding.href}>
              <div className='menu-logo' />
              <div className='menu-name'>{branding.cloudName}</div>
            </a>
          ) : (
            <div className='navbar-brand'>
              <div className='menu-logo' />
              <div className='menu-name'>{branding.cloudName}</div>
            </div>
          )
        }
      </div>
      {screenXS ? (
        <>
          <IconButton
            onClick={handleMenuButtonClick}
            className='navbar-burgerButton'
            size='large'
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            open={Boolean(isDrawerOpen)}
            onClose={handleMenuClose}
            keepMounted
          >
            <Tabs
              value={currentTabIndex}
              orientation='vertical'
              className='navBarDrawer-tabs'
            >
              {tabs.map(({ label, href }, index) => (
                <Tab
                  key={index}
                  component='a'
                  label={label[locale]}
                  href={href}
                  data-pjax
                  onClick={(event) => {
                    handleTabClick(event, index, href);
                  }}
                />
              ))}
            </Tabs>
          </Drawer>
        </>
      ) : (
        <Tabs
          value={currentTabIndex}
          variant='scrollable'
          scrollButtons='auto'
        >
          {tabs.map(({ label, href, icon }, index) => {
            if (isCalendarHref(href)) {
              icon = (
                <Badge color='primary' badgeContent='1' variant='dot' invisible={!hasPendingAppointments}>
                  {icon}
                </Badge>
              );
            }

            return (
              <Tab
                key={index}
                component='a'
                label={label[locale]}
                icon={icon}
                href={href}
                data-pjax
                onClick={(event) => {
                  handleTabClick(event, index, href);
                }}
              />
            );
          })}
        </Tabs>
      )}
      {videoconference &&
        <Button className='videoconference-button' onClick={handleVideoconferenceClick} sx={{ borderRadius: 0 }}>
          <CallIcon />
          {!screenSM && i18n._('Videoconference')}
          <UpIcon sx={videoConsultationButtonIconSx} />
        </Button>}

      {hasSupportHref && (
        <Stack direction='row' spacing={1} alignItems='center'>
          <Button startIcon={<HelpOutlineIcon />} color='primary' href='https://parsysassist.freshdesk.com/fr/support/tickets/new' target='_blank' rel='noopener noreferrer'>
            {i18n._('Support')}
          </Button>
        </Stack>
      )}
    </div>
  );
};

export default NavBar;
